import { ReactElement, useRef } from 'react';
import { Component as AccountIcon } from 'icons/icon-ui--my-account.svg';
import { Component as FavoriteIcon } from 'icons/icon-ui--favorites.svg';
import { Component as BoxIcon } from 'icons/icon-box.svg';
import { Component as RedeemGiftIcon } from 'icons/icon-ui--redeem-gift.svg';
import { Component as ReferAndEarnIcon } from 'icons/icon-ui--refer-and-earn.svg';
import { Component as GiftIcon } from 'icons/icon-ui--gift.svg';
import { Component as AdminIcon } from 'icons/icon-ui--admin.svg';

import useCurrentUser from 'hooks/useCurrentUser';
import dropdownStyle from '../account-menu/AccountMenu.module.scss';
import sidebarStyle from '../header/Header.module.scss';
import Link from '../shared/link/Link';
import { Route } from '../../utils/config';
import { Button } from '../shared/button/Button';

type Props = {
  isInHamburger?: boolean;
  onClose: () => void;
}

export function MenuSection({
  isInHamburger = true,
  onClose,
}: Props): ReactElement {
  const { currentUser } = useCurrentUser();
  const logoutButtonRef = useRef<HTMLFormElement>(null);
  const {
    isEmployee,
    hasFavoritesEnabled,
    hasGiveaways,
    giveawayCount,
    isAdmin,
  } = currentUser;

  const handleLogOut = (e) => {
    e.preventDefault();
    document.cookie = `account_creation_email=; path=/; expires=${new Date(0).toUTCString()}`;
    logoutButtonRef?.current?.submit();
  };

  return (
    <>
      <div className={
        isInHamburger
          ? sidebarStyle.sidebar_nav_list
          : dropdownStyle.section_container
      }
      >
        <Link
          href={Route.PROFILE}
          onClick={onClose}
          onModalClose={onClose}
          data-cy="header-my-account"
          className={({ isActive }) => ([
            'c-nav-header__sublink',
            isActive ? 'is-active' : '',
          ].join(' '))}
        >
          <div className={
            isInHamburger
              ? sidebarStyle.sidebar_link_content
              : dropdownStyle.link_container
          }
          >
            <AccountIcon />
            My Account
          </div>
        </Link>
        {hasFavoritesEnabled && (
          <div className={dropdownStyle.fix}>
            <Link
              href={Route.MY_FAVORITES}
              onClick={onClose}
              onModalClose={onClose}
              data-cy="header-my-favorites"
              className={({ isActive }) => ([
                'c-nav-header__sublink',
                isActive ? 'is-active' : '',
              ].join(' '))}
            >
              <div className={
                isInHamburger
                  ? sidebarStyle.sidebar_link_content
                  : dropdownStyle.link_container
              }
              >
                <FavoriteIcon />
                My Favorites
              </div>
            </Link>
          </div>
        )}
      </div>
      {isInHamburger && <li className={sidebarStyle.sidebar_divider} />}
      <div className={
        isInHamburger
          ? sidebarStyle.sidebar_nav_list
          : dropdownStyle.section_container
      }
      >
        <div className={dropdownStyle.wrapper}>
          <Link
            href={Route.REDEEM_CODE}
            onClick={onClose}
            onModalClose={onClose}
            data-cy="header-redeem-gift"
            className={({ isActive }) => ([
              'c-nav-header__sublink',
              isActive ? 'is-active' : '',
            ].join(' '))}
          >
            <div className={
              isInHamburger
                ? `${sidebarStyle.sidebar_link_content}`
                : `${dropdownStyle.link_container}`
            }
            >
              <RedeemGiftIcon style={{
                position: 'relative',
                zIndex: 50,
                flexShrink: 0,
              }}
              />
              <div className={
                isInHamburger
                  ? sidebarStyle.redeem_padding
                  : ''
              }
              >
                Redeem Gift or Discount
              </div>
            </div>
          </Link>
        </div>

        <Link
          href={Route.REFER}
          onClick={onClose}
          onModalClose={onClose}
          data-cy="header-refer"
          className={({ isActive }) => ([
            'c-nav-header__sublink',
            isActive ? 'is-active' : '',
          ].join(' '))}
        >
          <div className={
            isInHamburger
              ? sidebarStyle.sidebar_link_content
              : dropdownStyle.link_container
          }
          >
            <ReferAndEarnIcon />
            Refer and Earn
          </div>
        </Link>
        {!isEmployee && isInHamburger && (
          <Link
            href={Route.GIFTS}
            onModalClose={onClose}
            data-cy="header-share-a-box"
            className={({ isActive }) => ([
              'c-nav-header__sublink',
              isActive ? 'is-active' : '',
            ].join(' '))}
            reload
          >
            <div className={
              isInHamburger
                ? sidebarStyle.sidebar_link_content
                : dropdownStyle.link_container
            }
            >
              <GiftIcon />
              Gifts
            </div>
          </Link>
        )}
      </div>
      {isInHamburger && <li className={sidebarStyle.sidebar_divider} />}
      <div className={
        isInHamburger
          ? sidebarStyle.sidebar_nav_list
          : dropdownStyle.section_container
      }
      >
        {isAdmin && isInHamburger && (
          <Link
            href={Route.ADMIN}
            onModalClose={onClose}
            data-cy="header-admin"
            className={({ isActive }) => ([
              'c-nav-header__sublink',
              isActive ? 'is-active' : '',
            ].join(' '))}
            reload
          >
            <div className={
              isInHamburger
                ? sidebarStyle.sidebar_link_content
                : dropdownStyle.link_container
            }
            >
              <AdminIcon />
              Admin
            </div>
          </Link>
        )}
        {!isEmployee && (
          <Link
            href={Route.SHARE_A_BOX}
            onModalClose={onClose}
            data-cy="header-share-a-box"
            className={({ isActive }) => ([
              'c-nav-header__sublink',
              isActive ? 'is-active' : '',
            ].join(' '))}
          >
            <div className={
              isInHamburger
                ? sidebarStyle.sidebar_link_content
                : dropdownStyle.link_container
            }
            >
              <BoxIcon height="12px" width="12px" />
              Share a Box

              {hasGiveaways && (
                <span className={`c-badge c-badge--info ${dropdownStyle.giveaway_count}`}>
                  {giveawayCount}
                </span>
              )}
            </div>
          </Link>
        )}
      </div>
      {isInHamburger && <li className={sidebarStyle.sidebar_divider} />}
      {isInHamburger && (
        <div className={sidebarStyle.logged_out_button_containe}>
          <form
            method="POST"
            action="/users/sign_out"
            onSubmit={handleLogOut}
            ref={logoutButtonRef}
          >
            <input type="hidden" name="_method" value="delete" />
            <Button
              type="submit"
              text="Log Out"
              style={isInHamburger ? {
                borderColor: '#DEE2E6',
                color: '#212529',
                fontWeight: 400,
                borderRadius: '4px',
                width: '100%',
                background: '#FFF',
                marginTop: '20px',
              } : {
                borderColor: '#DEE2E6',
                color: '#212529',
                fontWeight: 400,
                borderRadius: '4px',
                width: '171px',
                background: '#FFF',
              }}
            />
          </form>
        </div>
      )}
    </>
  );
}
